/* eslint-disable react-refresh/only-export-components */
import { type ReactNode, useId, useState } from "react";

import { AdminButton } from "@/components/admin/button";
import { AdminIcon, type IconName } from "@/components/admin/icon";

/**
 * Briques communes aux écrans du CMS.
 *
 * Le CMS tenait dans une seule page de près de sept mille pixels où trois
 * domaines sans rapport se succédaient, chacun rendu avec ses propres styles
 * écrits à la main. Regrouper ici la poignée de motifs qui reviennent partout
 * évite que chaque écran réinvente sa mise en page — c'est ainsi qu'étaient
 * apparues des barres d'actions débordant de l'écran sur mobile.
 */

/* ─────────────────────────── Champ expliqué ─────────────────────────── */

/**
 * Un champ avec son libellé et, si besoin, l'explication de ce qu'il fait.
 *
 * Les libellés d'origine étaient les noms techniques du modèle de données —
 * « Eyebrow », « CTA », « Tone », « Big » — que seule une personne ayant écrit
 * le code pouvait comprendre. La ligne d'aide dit ce que le visiteur verra,
 * pas comment le champ s'appelle en base.
 */
export function CmsField({
	label,
	hint,
	children,
	span,
}: {
	label: string;
	hint?: string;
	children: ReactNode;
	/** Occupe toute la largeur de la grille (textes longs, adresses). */
	span?: boolean;
}) {
	return (
		<div style={{ minWidth: 0, gridColumn: span ? "1 / -1" : undefined }}>
			<label
				style={{
					display: "block",
					fontSize: 12,
					fontWeight: 600,
					color: "var(--a-ink)",
					marginBottom: 3,
				}}
			>
				{label}
			</label>
			{hint && (
				<p
					className="muted"
					style={{ fontSize: 11, margin: "0 0 5px", lineHeight: 1.35 }}
				>
					{hint}
				</p>
			)}
			{children}
		</div>
	);
}

/** Style commun des saisies — repris tel quel des écrans d'origine. */
export const cmsInput: React.CSSProperties = {
	width: "100%",
	maxWidth: "100%",
	padding: "8px 10px",
	border: "1px solid var(--a-border)",
	borderRadius: 6,
	fontSize: 13,
	background: "var(--a-bg)",
	color: "var(--a-ink)",
	outline: "none",
};

/**
 * Grille de champs qui se réorganise selon la place disponible.
 *
 * `auto-fit` plutôt qu'un nombre de colonnes figé : sur un téléphone les champs
 * s'empilent d'eux-mêmes, sans média query à maintenir. `minmax(0, …)` est
 * indispensable — sans lui, une saisie large force la colonne à s'étendre et
 * fait déborder toute la page horizontalement.
 */
export function CmsGrid({
	children,
	min = 200,
}: {
	children: ReactNode;
	min?: number;
}) {
	return (
		<div
			style={{
				display: "grid",
				gridTemplateColumns: `repeat(auto-fit, minmax(min(${min}px, 100%), 1fr))`,
				gap: 12,
				alignItems: "start",
			}}
		>
			{children}
		</div>
	);
}

/* ─────────────────────────── Bloc repliable ─────────────────────────── */

/**
 * Section repliable, avec le nombre d'éléments qu'elle contient et un rappel
 * de l'endroit où ce contenu apparaît sur le site.
 *
 * Replié par défaut : la page d'origine affichait tout en même temps, si bien
 * qu'atteindre les conditions générales demandait de faire défiler plusieurs
 * milliers de pixels. Le compteur dans l'en-tête évite d'avoir à ouvrir un bloc
 * pour savoir s'il contient quelque chose.
 */
export function CmsSection({
	title,
	where,
	icon,
	count,
	countLabel,
	defaultOpen = false,
	action,
	children,
}: {
	title: string;
	/** Où ce contenu apparaît, du point de vue du visiteur. */
	where?: string;
	icon?: IconName;
	count?: number;
	countLabel?: (n: number) => string;
	defaultOpen?: boolean;
	/** Bouton propre à la section (« Ajouter »), masqué quand elle est repliée. */
	action?: ReactNode;
	children: ReactNode;
}) {
	const [open, setOpen] = useState(defaultOpen);
	const id = useId();

	return (
		<section
			className="card"
			style={{ marginBottom: 12, overflow: "hidden" }}
			data-testid={`cms-section-${title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
		>
			<div
				style={{
					display: "flex",
					flexWrap: "wrap",
					alignItems: "center",
					gap: 10,
					padding: "12px 14px",
					borderBottom: open ? "1px solid var(--a-border)" : "none",
				}}
			>
				<button
					type="button"
					onClick={() => setOpen((v) => !v)}
					aria-expanded={open}
					aria-controls={id}
					style={{
						display: "flex",
						alignItems: "center",
						gap: 10,
						flex: "1 1 220px",
						minWidth: 0,
						background: "transparent",
						cursor: "pointer",
						textAlign: "left",
						padding: 0,
					}}
				>
					<span
						style={{
							display: "grid",
							placeItems: "center",
							width: 30,
							height: 30,
							flexShrink: 0,
							borderRadius: 8,
							background: "var(--a-bg-2)",
							color: "var(--a-ink-2)",
						}}
						aria-hidden="true"
					>
						<AdminIcon n={icon ?? "layout"} s={15} />
					</span>
					<span style={{ minWidth: 0 }}>
						<span
							style={{
								display: "block",
								fontSize: 14,
								fontWeight: 700,
								color: "var(--a-ink)",
							}}
						>
							{title}
							{typeof count === "number" && (
								<span className="muted" style={{ fontWeight: 500 }}>
									{" · "}
									{countLabel ? countLabel(count) : count}
								</span>
							)}
						</span>
						{where && (
							<span
								className="muted"
								style={{ display: "block", fontSize: 11, lineHeight: 1.35 }}
							>
								{where}
							</span>
						)}
					</span>
					<span
						style={{
							marginLeft: "auto",
							flexShrink: 0,
							color: "var(--a-ink-2)",
							transform: open ? "rotate(180deg)" : "none",
							transition: "transform .15s",
						}}
						aria-hidden="true"
					>
						<AdminIcon n="chevD" s={15} />
					</span>
				</button>
				{open && action}
			</div>
			{open && (
				<div id={id} style={{ padding: 14 }}>
					{children}
				</div>
			)}
		</section>
	);
}

/* ─────────────────────── Élément d'une liste ─────────────────────── */

/**
 * Une entrée répétable (un volet du carrousel, une carte promo…), numérotée.
 *
 * Le numéro et la légende comptent : sans eux, une dizaine d'encadrés
 * identiques se suivent et l'on ne sait plus lequel on est en train de
 * modifier, ni dans quel ordre ils apparaîtront sur le site.
 */
export function CmsItem({
	index,
	label,
	onRemove,
	removeLabel = "Supprimer",
	children,
}: {
	index: number;
	label?: string;
	onRemove?: () => void;
	removeLabel?: string;
	children: ReactNode;
}) {
	return (
		<div
			style={{
				border: "1px solid var(--a-border)",
				borderRadius: 10,
				padding: 12,
				marginBottom: 10,
				background: "var(--a-bg)",
			}}
		>
			<div
				style={{
					display: "flex",
					flexWrap: "wrap",
					alignItems: "center",
					gap: 8,
					marginBottom: 10,
				}}
			>
				<span
					style={{
						display: "grid",
						placeItems: "center",
						minWidth: 22,
						height: 22,
						padding: "0 6px",
						borderRadius: 6,
						background: "var(--a-bg-2)",
						fontSize: 11,
						fontWeight: 700,
						color: "var(--a-ink-2)",
					}}
				>
					{index + 1}
				</span>
				{label && (
					<span
						style={{
							fontSize: 12,
							fontWeight: 600,
							color: "var(--a-ink-2)",
							minWidth: 0,
							overflow: "hidden",
							textOverflow: "ellipsis",
							whiteSpace: "nowrap",
						}}
					>
						{label}
					</span>
				)}
				{onRemove && (
					<button
						type="button"
						onClick={onRemove}
						aria-label={removeLabel}
						title={removeLabel}
						style={{
							marginLeft: "auto",
							display: "inline-flex",
							alignItems: "center",
							gap: 4,
							color: "var(--a-danger)",
							cursor: "pointer",
							background: "transparent",
							fontSize: 12,
						}}
					>
						<AdminIcon n="trash" s={13} />
					</button>
				)}
			</div>
			{children}
		</div>
	);
}

/* ────────────────────────── Barre d'actions ────────────────────────── */

/**
 * En-tête d'écran avec ses actions.
 *
 * `flexWrap` n'est pas un détail : les barres d'origine alignaient trois
 * boutons sur une seule ligne, ce qui produisait une largeur de 426 pixels
 * sur un écran de 390 et faisait défiler la page entière de côté. Ici les
 * actions passent à la ligne au lieu de pousser le contenu.
 */
export function CmsToolbar({
	title,
	subtitle,
	dirty,
	saving,
	onSave,
	saveLabel = "Enregistrer",
	extra,
}: {
	title: string;
	subtitle?: string;
	/** Des modifications attendent d'être enregistrées. */
	dirty?: boolean;
	saving?: boolean;
	onSave?: () => void;
	saveLabel?: string;
	extra?: ReactNode;
}) {
	return (
		<div
			style={{
				display: "flex",
				flexWrap: "wrap",
				alignItems: "center",
				gap: 10,
				marginBottom: 14,
			}}
		>
			<div style={{ flex: "1 1 240px", minWidth: 0 }}>
				<h2
					style={{
						fontSize: 15,
						fontWeight: 700,
						margin: 0,
						color: "var(--a-ink)",
					}}
				>
					{title}
				</h2>
				{subtitle && (
					<p
						className="muted"
						style={{ fontSize: 12, margin: "2px 0 0", lineHeight: 1.4 }}
					>
						{subtitle}
					</p>
				)}
			</div>
			<div
				style={{
					display: "flex",
					flexWrap: "wrap",
					alignItems: "center",
					gap: 8,
				}}
			>
				{extra}
				{onSave && (
					<>
						{dirty && (
							<span
								style={{
									fontSize: 11,
									fontWeight: 600,
									color: "var(--a-warning, #B45309)",
									whiteSpace: "nowrap",
								}}
								data-testid="cms-dirty"
							>
								Modifications non enregistrées
							</span>
						)}
						<AdminButton
							variant="primary"
							onClick={onSave}
							disabled={saving}
							data-testid="cms-save"
						>
							<AdminIcon n="check" s={14} />
							{saving ? "Enregistrement…" : saveLabel}
						</AdminButton>
					</>
				)}
			</div>
		</div>
	);
}

/* ───────────────────────── Vignette d'emplacement ───────────────────── */

type Zone = "hero" | "band" | "cards" | "text" | "list" | "footer";

/**
 * Petit schéma de page montrant où le bloc édité apparaît.
 *
 * Une capture du vrai site serait plus parlante mais se périmerait au premier
 * changement de maquette, sans que personne ne le remarque. Ce schéma abstrait
 * ne prétend pas être fidèle : il situe, et c'est tout ce qu'on lui demande.
 */
export function CmsPlacement({ zone, text }: { zone: Zone; text: string }) {
	const on = "var(--a-terracotta, #C6563A)";
	const off = "var(--a-border)";
	const rows: Record<Zone, Array<"on" | "off" | "split" | "trio">> = {
		hero: ["on", "off", "off"],
		band: ["off", "on", "off"],
		cards: ["off", "split", "off"],
		text: ["off", "on", "on"],
		list: ["off", "trio", "off"],
		footer: ["off", "off", "on"],
	};

	return (
		<div
			style={{
				display: "flex",
				alignItems: "center",
				gap: 10,
				padding: "9px 11px",
				marginBottom: 12,
				background: "var(--a-bg-2)",
				border: "1px solid var(--a-border)",
				borderRadius: 8,
			}}
		>
			<div
				aria-hidden="true"
				style={{
					display: "flex",
					flexDirection: "column",
					gap: 2,
					width: 34,
					flexShrink: 0,
					padding: 3,
					background: "var(--a-bg)",
					border: `1px solid ${off}`,
					borderRadius: 4,
				}}
			>
				{rows[zone].map((kind, i) => {
					if (kind === "split" || kind === "trio") {
						const n = kind === "split" ? 2 : 3;
						return (
							<div
								key={i}
								style={{ display: "flex", gap: 2, height: kind === "trio" ? 6 : 9 }}
							>
								{Array.from({ length: n }, (_, j) => (
									<div key={j} style={{ flex: 1, background: on, borderRadius: 1 }} />
								))}
							</div>
						);
					}
					return (
						<div
							key={i}
							style={{
								height: kind === "on" ? 11 : 5,
								background: kind === "on" ? on : off,
								borderRadius: 1,
							}}
						/>
					);
				})}
			</div>
			<p style={{ fontSize: 11.5, margin: 0, lineHeight: 1.4, minWidth: 0 }}>
				{text}
			</p>
		</div>
	);
}
